import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import useMenuNavigation from '../../hooks/useMenuNavigation/useMenuNavigation';
import type {
  ListComplexDropdownItem,
  ListComplexDropdownItemRest,
  UseListComplexDropdownParams,
  UseListComplexDropdownReturn,
} from './types';

/**
 * Lógica del `ListComplexDropdown`. Concentra la traducción del nombre
 * accesible, la navegación por teclado (`useMenuNavigation`) y la separación
 * de las props conocidas de cada fila de las de passthrough, dejando el
 * componente como JSX puro.
 */
function useListComplexDropdown({ ariaLabel, items, onClick, shouldAutoFocusFirstItem }: UseListComplexDropdownParams): UseListComplexDropdownReturn {
  const { t } = useTranslation();
  const resolvedAriaLabel = ariaLabel ?? t('common:Options');

  const onActivate = useCallback(
    (index: number): void => {
      const item = items[index];
      if (item) onClick(item);
    },
    [items, onClick],
  );

  const { getItemProps: getMenuItemProps, getMenuProps } = useMenuNavigation({
    isItemFocusable: () => true,
    itemCount: items.length,
    onActivate,
    shouldAutoFocusFirstItem,
  });

  // Añade la activación por click a las props ARIA de la fila, para que el
  // componente quede como JSX puro (click y Enter/Space comparten despacho).
  const getItemProps = useCallback(
    (index: number) => {
      function handleClick(): void {
        onActivate(index);
      }
      return { ...getMenuItemProps(index), onClick: handleClick };
    },
    [getMenuItemProps, onActivate],
  );

  const resolveItem = useCallback((item: ListComplexDropdownItem) => {
    const { center, className, guid, left, previewContent, right, ...rest } = item;
    return {
      center,
      className,
      guid,
      left,
      previewContent,
      restProps: rest as ListComplexDropdownItemRest,
      right,
    };
  }, []);

  return {
    getItemProps,
    getMenuProps,
    resolveItem,
    resolvedAriaLabel,
  };
}

export default useListComplexDropdown;
